


























import Vue from 'vue';
import { ApiHttpClient } from '@/globals';
import TableProvider from '@/api/TableProvider';
import { ensureRegion } from '@/api/RegionProvider';
import { deriveMaskName, readExpressions, readFrames, IFaceFrame } from '@/util/faceResolve';

interface ResolvedExpression {
    name: string;
    faceId: number;
    frames: IFaceFrame[];
    maskFile?: string;
}

//  _TextureName1.._TextureName28 — request fileresolve on all frame columns so the
//  API returns _TextureName{k}_file filenames.
const FACEANIM_FILE_COLS: string[] = [];
for (let k = 1; k <= 28; k++) {
    FACEANIM_FILE_COLS.push('_TextureName' + k);
}

export default Vue.extend({
    props: {
        partsId: { type: Number, required: true },
        region: { type: String, default: undefined },
    },
    data() {
        return {
            loading: true,
            notFace: false,
            classFallback: false,
            expressions: [] as ResolvedExpression[],
        };
    },
    computed: {
        //  Render nothing until we've resolved a real face — no empty heading flash
        //  on non-face PARTS items (hair, etc.).
        show(): boolean {
            return !this.loading && !this.notFace && (this.classFallback || this.expressions.length > 0);
        },
    },
    created() {
        this.resolve();
    },
    watch: {
        partsId() { this.resolve(); },
        region() { this.resolve(); },
    },
    methods: {
        ddsUrl(name: string): string {
            const region = ensureRegion(this.region, true);
            return `${ApiHttpClient.defaults.baseURL}/server/${region}/dds/${name}/png`;
        },
        fileList(e: ResolvedExpression): string {
            return e.frames.map((f) => f.file).join(', ');
        },
        delayList(e: ResolvedExpression): string {
            return e.frames.map((f) => f.delayMs).join(' / ');
        },
        onImgError(evt: Event): void {
            (evt.target as HTMLElement).classList.add('img-broken');
        },
        async resolve(): Promise<void> {
            this.loading = true;
            this.notFace = false;
            this.classFallback = false;
            this.expressions = [];
            try {
                //  virt.partstable is the merged superset; the base `partstable` is an
                //  incomplete subset (missing many items' rows, e.g. 297905). Only this
                //  table takes the virt. prefix — actorfacetable/faceanimationtable below
                //  exist only un-prefixed (virt.* -> "No such table").
                const parts: any = await TableProvider.getTableRow('virt.partstable', this.partsId, this.region);
                if (!parts || Number(parts._Parts) !== 0) {
                    this.notFace = true;
                    return;
                }

                const actorFaceId = Number(parts._ActorFaceID);
                if (!(actorFaceId > 0)) {
                    this.classFallback = true;
                    return;
                }

                const af: any = await TableProvider.getTableRow('actorfacetable', actorFaceId, this.region);
                const useMask = !!af._UseMaskTexture;
                const exprs = readExpressions(af);
                const faceIds = exprs.map((e) => e.faceId);
                const animMap: any = await TableProvider.getTableRows(
                    'faceanimationtable', faceIds, this.region, { fileresolve: FACEANIM_FILE_COLS as any });

                const resolved: ResolvedExpression[] = [];
                for (const e of exprs) {
                    const anim = animMap[e.faceId];
                    const frames = anim ? readFrames(anim) : [];
                    if (!frames.length) {
                        continue;
                    }

                    resolved.push({
                        name: e.name,
                        faceId: e.faceId,
                        frames,
                        maskFile: useMask ? deriveMaskName(frames[0].file) : undefined,
                    });
                }

                this.expressions = resolved;
            } catch (err) {
                //  Fail quiet: on any resolution error, show no section rather than a broken one.
                this.notFace = true;
            } finally {
                this.loading = false;
            }
        },
    },
});
